import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronDown,
  Globe2,
  Layers,
  LineChart,
  Loader2,
  Sparkles,
} from 'lucide-react';
import {
  buildAnalogPricePath,
  type AnalogLens,
  type StockAnalogLensData,
  type StockAnalogMatch,
} from '@/mocks/stockAnalogMock';
import { AnalogForwardMiniChart, sharedSessionDomain } from './AnalogForwardMiniChart';
import { AnalogLensOutlookVisual } from './AnalogLensOutlookVisual';
import { AnalogTapeFingerprint } from './AnalogTapeFingerprint';

export function formatPct(value: number) {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

export function formatDateShort(value: string): string {
  try {
    const date = new Date(`${value}T12:00:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return value;
  }
}

export function pctChipClass(value: number) {
  if (value > 0) return 'text-emerald-700';
  if (value < 0) return 'text-rose-700';
  return 'text-gray-600';
}

const LENS_ICONS: Record<AnalogLens, typeof Sparkles> = {
  situational: Sparkles,
  mechanical: LineChart,
  sector: Layers,
  macro: Globe2,
};

export const LENS_ACCENT: Record<
  StockAnalogLensData['accent'],
  { text: string; bg: string; border: string; dot: string; badge: string }
> = {
  emerald: {
    text: 'text-emerald-800',
    bg: 'bg-emerald-50/60',
    border: 'border-emerald-100',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-500/10 text-emerald-700',
  },
  blue: {
    text: 'text-blue-800',
    bg: 'bg-blue-50/60',
    border: 'border-blue-100',
    dot: 'bg-blue-500',
    badge: 'bg-blue-500/10 text-blue-700',
  },
  violet: {
    text: 'text-violet-800',
    bg: 'bg-violet-50/60',
    border: 'border-violet-100',
    dot: 'bg-violet-500',
    badge: 'bg-violet-500/10 text-violet-700',
  },
};

function usesGspcChart(match: StockAnalogMatch, lensChartSymbol?: string): boolean {
  return lensChartSymbol === '^GSPC' || match.chartSymbol === '^GSPC';
}

function forwardReturnChips(forwardReturns: StockAnalogMatch['forwardReturns']) {
  const chips: { label: string; value: number }[] = [];
  if (forwardReturns.t5 != null) chips.push({ label: '5d', value: forwardReturns.t5 });
  if (forwardReturns.t10 != null) chips.push({ label: '10d', value: forwardReturns.t10 });
  if (forwardReturns.t20 != null) chips.push({ label: '20d', value: forwardReturns.t20 });
  if (forwardReturns.t40 != null) chips.push({ label: '40d', value: forwardReturns.t40 });
  return chips;
}

export function AnalogMatchDetail({
  match,
  lensId = 'situational',
  lensChartSymbol,
  referenceTape,
  referenceTapeLabel = 'Today',
  accent = 'emerald',
}: {
  match: StockAnalogMatch;
  lensId?: AnalogLens;
  lensChartSymbol?: string;
  referenceTape?: Record<string, unknown> | null;
  referenceTapeLabel?: string;
  accent?: StockAnalogLensData['accent'];
}) {
  const gspcChart = usesGspcChart(match, lensChartSymbol);
  const chartPath =
    match.pricePath?.length > 0
      ? match.pricePath
      : buildAnalogPricePath(match.dayReturnPct, match.forwardReturns);
  const context = match.context?.trim() || '';
  const whyMatches = match.whyThisMatches?.trim() || '';
  const whatNext = match.whatHappenedNext?.trim() || '';
  const reasons = (match.matchReasons ?? []).filter(Boolean).slice(0, 6);
  // Skip similarity-label-only reasons that duplicate the headline ranking chrome.
  const displayReasons = reasons.filter(
    (r) =>
      !/^#\d+\s/.test(r) &&
      !/^(closest|secondary)\b.*\bmatch$/i.test(r.trim()) &&
      !/^#\d+\s+(situational|mechanical|regime|macro)\s+match$/i.test(r.trim()),
  );
  const keyDifferences = (match.keyDifferences ?? [])
    .map((d) => d.trim())
    .filter(Boolean)
    .slice(0, 4);
  const fwdChips = forwardReturnChips(match.forwardReturns ?? {});
  const relatedCharts =
    lensId === 'situational' || lensId === 'mechanical' || lensId === 'macro'
      ? (match.relatedCharts ?? [])
      : [];
  const hasNarrative = Boolean(
    context ||
      whyMatches ||
      whatNext ||
      displayReasons.length ||
      keyDifferences.length ||
      fwdChips.length,
  );

  const relatedPaths = relatedCharts.map((chart) =>
    chart.pricePath?.length > 0
      ? chart.pricePath
      : buildAnalogPricePath(chart.dayReturnPct, chart.forwardReturns ?? {}),
  );
  const stackDomain = sharedSessionDomain(
    [chartPath, ...relatedPaths].filter((p) => p.length > 0),
  );

  return (
    <div className="border-t border-gray-100 bg-white px-2 py-3">
      {hasNarrative ? (
        <div className="mb-4 space-y-4 px-2">
          {whyMatches ? (
            <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                Why this was selected
              </p>
              <p className="text-sm leading-relaxed text-gray-800">{whyMatches}</p>
            </div>
          ) : null}
          <AnalogTapeFingerprint
            referenceTape={referenceTape}
            analogTape={match.dayStats}
            referenceLabel={referenceTapeLabel}
            analogLabel={formatDateShort(match.date)}
            accent={accent}
          />
          {displayReasons.length > 0 ? (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                What lined up with today
              </p>
              <ul className="space-y-1.5">
                {displayReasons.map((reason) => (
                  <li key={reason} className="flex gap-2 text-xs text-gray-600">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500/70" />
                    <span className="leading-relaxed">{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {keyDifferences.length > 0 ? (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Where it differs
              </p>
              <ul className="space-y-1.5">
                {keyDifferences.map((diff) => (
                  <li key={diff} className="flex gap-2 text-xs text-gray-500">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400/80" />
                    <span className="leading-relaxed">{diff}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {context ? (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                What happened that day
              </p>
              <p className="text-sm leading-relaxed text-gray-700">{context}</p>
            </div>
          ) : null}
          {whatNext ? (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                What happened next
              </p>
              <p className="text-xs leading-relaxed text-gray-600">{whatNext}</p>
            </div>
          ) : null}
          {fwdChips.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {fwdChips.map((chip) => (
                <span
                  key={chip.label}
                  className={`rounded-md bg-gray-50 px-2 py-1 text-[11px] font-medium ${pctChipClass(chip.value)}`}
                >
                  {chip.label} {formatPct(chip.value)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {gspcChart ? (
        <div className="mb-4">
          <p className="mb-2 px-2 text-xs font-medium text-gray-500">S&P 500 (^GSPC)</p>
          <AnalogForwardMiniChart
            path={chartPath}
            anchorDate={match.date}
            anchorClose={match.anchorClose}
            sessionDomain={stackDomain}
            hideXAxis={relatedCharts.length > 0}
          />
        </div>
      ) : (
        <AnalogForwardMiniChart
          path={chartPath}
          anchorDate={match.date}
          anchorClose={match.anchorClose}
          sessionDomain={stackDomain}
          hideXAxis={relatedCharts.length > 0}
        />
      )}
      {relatedCharts.length > 0 ? (
        <div className="space-y-3 border-t border-gray-100 pt-3">
          <p className="px-2 text-xs font-medium text-gray-500">
            Mentioned in the session
          </p>
          {relatedCharts.map((chart, index) => {
            const relatedPath = relatedPaths[index] ?? [];
            const isLast = index === relatedCharts.length - 1;
            return (
              <div key={chart.symbol}>
                <div className="mb-1 flex items-center gap-2 px-2">
                  <p className="text-xs text-gray-500">{chart.label}</p>
                  <span className={`text-xs font-medium ${pctChipClass(chart.dayReturnPct)}`}>
                    {formatPct(chart.dayReturnPct)}
                  </span>
                </div>
                <AnalogForwardMiniChart
                  path={relatedPath}
                  anchorDate={match.date}
                  anchorClose={chart.anchorClose}
                  significantMoves={chart.significantMoves}
                  sessionDomain={stackDomain}
                  hideXAxis={!isLast}
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function AnalogListRow({
  match,
  rank,
  accent,
  lensId,
  lensChartSymbol,
  isOpen,
  onToggle,
  referenceTape,
  referenceTapeLabel,
}: {
  match: StockAnalogMatch;
  rank: number;
  accent: StockAnalogLensData['accent'];
  lensId: AnalogLens;
  lensChartSymbol?: string;
  isOpen: boolean;
  onToggle: () => void;
  referenceTape?: Record<string, unknown> | null;
  referenceTapeLabel?: string;
}) {
  const colors = LENS_ACCENT[accent];
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
          isOpen ? 'bg-gray-50' : ''
        }`}
      >
        <span className="w-5 shrink-0 text-xs font-medium text-gray-400">{rank + 1}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium text-gray-900">{formatDateShort(match.date)}</span>
            <span className={`text-sm font-medium ${pctChipClass(match.dayReturnPct)}`}>
              {formatPct(match.dayReturnPct)}
            </span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${colors.badge}`}>
              {(match.similarityScore * 100).toFixed(0)}% match
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-gray-500">{match.headline}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <AnalogMatchDetail
              match={match}
              lensId={lensId}
              lensChartSymbol={lensChartSymbol}
              referenceTape={referenceTape}
              referenceTapeLabel={referenceTapeLabel}
              accent={accent}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

type LensLoadingCopy = Partial<Record<AnalogLens, { collapsed: string; expanded: string; empty: string }>>;

const DEFAULT_LOADING_COPY: LensLoadingCopy = {
  situational: {
    collapsed: 'Loading catalyst analogs…',
    expanded: 'Loading curated catalyst analogs…',
    empty: 'No catalyst_analogs file found for this ticker/date.',
  },
  mechanical: {
    collapsed: 'Matching 5y price history…',
    expanded: 'Fetching FMP history and matching price fingerprints…',
    empty: 'Not enough history to form mechanical analogs for this ticker.',
  },
  sector: {
    collapsed: 'Loading SOX sector analogs…',
    expanded: 'Loading SOX semiconductor sector analogs…',
    empty: 'No sector proxy analogs configured for this ticker.',
  },
  macro: {
    collapsed: 'Loading macro / regime analogs…',
    expanded: 'Loading market macro / regime analogs…',
    empty: 'No macro / regime analogs available for this session.',
  },
};

export function LensAccordion({
  lens,
  isOpen,
  onToggle,
  expandedMatchDate,
  onToggleMatch,
  isLoading,
  loadingCopy = DEFAULT_LOADING_COPY,
  referenceDate,
  referenceTape,
  referenceTapeLabel,
}: {
  lens: StockAnalogLensData;
  isOpen: boolean;
  onToggle: () => void;
  expandedMatchDate: string | null;
  onToggleMatch: (date: string) => void;
  isLoading?: boolean;
  loadingCopy?: LensLoadingCopy;
  referenceDate?: string;
  referenceTape?: Record<string, unknown> | null;
  referenceTapeLabel?: string;
}) {
  const Icon = LENS_ICONS[lens.id];
  const copy = loadingCopy[lens.id];

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center gap-3 px-4 py-4 text-left transition-colors md:px-5 ${
          isOpen ? 'bg-gray-50' : 'hover:bg-gray-50/60'
        }`}
      >
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${isOpen ? 'bg-gray-900' : 'bg-gray-100'}`}>
          <Icon className={`h-4 w-4 ${isOpen ? 'text-white' : 'text-gray-600'}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-gray-900">{lens.label}</p>
            <span className="text-xs text-gray-400">{lens.subtitle}</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              {isLoading ? '…' : `${lens.matches.length} dates`}
            </span>
          </div>
          {!isOpen && isLoading && copy?.collapsed ? (
            <p className="mt-0.5 truncate text-xs text-gray-500">{copy.collapsed}</p>
          ) : null}
          {!isOpen && !isLoading && lens.matches.length === 0 ? (
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {lens.id === 'situational'
                ? 'No situational matches yet'
                : lens.id === 'sector'
                  ? 'No sector matches yet'
                  : lens.id === 'macro'
                    ? 'No macro / regime matches yet'
                    : 'No mechanical matches yet'}
            </p>
          ) : null}
        </div>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100">
              {isLoading ? (
                <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-8 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {copy?.expanded ?? 'Loading analogs…'}
                </div>
              ) : lens.matches.length === 0 ? (
                <div className="border-t border-gray-100 px-4 py-6 text-sm text-gray-500">
                  {copy?.empty ?? 'No matches available yet.'}
                </div>
              ) : (
                <div className="border-t border-gray-100">
                  {lens.aiSummary || lens.matches.length > 0 ? (
                    <div className="border-b border-gray-100 px-4 py-3.5 md:px-5">
                      <div className="mb-3 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-gray-400" />
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Outlook analysis
                        </p>
                      </div>
                      <AnalogLensOutlookVisual lens={lens} referenceDate={referenceDate} />
                      {lens.aiSummary ? (
                        <div className="mt-4 border-t border-gray-200/80 pt-4">
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                            Narrative read-through
                          </p>
                          <p className="text-sm leading-relaxed text-gray-700">{lens.aiSummary}</p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {lens.matches.map((match, index) => (
                    <AnalogListRow
                      key={match.date}
                      match={match}
                      rank={index}
                      accent={lens.accent}
                      lensId={lens.id}
                      lensChartSymbol={lens.chartSymbol}
                      isOpen={expandedMatchDate === match.date}
                      onToggle={() => onToggleMatch(match.date)}
                      referenceTape={referenceTape}
                      referenceTapeLabel={referenceTapeLabel}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
